import { Fragment, useEffect, useRef } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { ChevronUpIcon, TagIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Spinner } from '../ui';
import type { ServiceCategory } from '../../types/serviceCategory';

export interface ServiceBulkBarProps {
	count: number;
	/** Selected services the current search or filter is hiding. */
	hiddenCount: number;
	categories: ServiceCategory[];
	onAssign: (categoryId: number) => void;
	onClear: () => void;
	isAssigning: boolean;
	/**
	 * A gesture worth teaching at this exact moment, or null. Shown quietly
	 * inside the bar and retired once the provider has used the gesture.
	 */
	hint: string | null;
}

/**
 * The bar that rises once services are selected. This is the primary way
 * categories get assigned in bulk — dragging onto a category chip is the
 * shortcut, not the requirement, which is what keeps the feature usable on a
 * tablet and from the keyboard.
 */
const ServiceBulkBar = ({
	count,
	hiddenCount,
	categories,
	onAssign,
	onClear,
	isAssigning,
	hint,
}: ServiceBulkBarProps) => {
	// Hold the last real count so the bar doesn't read "0 selected" during the
	// leave transition.
	const lastCount = useRef(count);
	useEffect(() => {
		if (count > 0) lastCount.current = count;
	}, [count]);

	const shown = count > 0 ? count : lastCount.current;

	return (
		<Transition
			show={count > 0}
			as={Fragment}
			enter="transition duration-200 ease-out"
			enterFrom="translate-y-4 opacity-0"
			enterTo="translate-y-0 opacity-100"
			leave="transition duration-150 ease-in"
			leaveFrom="translate-y-0 opacity-100"
			leaveTo="translate-y-4 opacity-0"
		>
			<div className="bulk-bar" role="region" aria-label="Selected services">
				<div className="bulk-bar__inner">
					<div className="bulk-bar__count">
						<span className="bulk-bar__badge">{shown}</span>
						<div className="bulk-bar__text">
							<span className="bulk-bar__label">
								service{shown !== 1 ? 's' : ''} selected
							</span>
							{hiddenCount > 0 && (
								<span className="bulk-bar__sub">
									{hiddenCount} not in view
								</span>
							)}
							{hint && !hiddenCount && <span className="bulk-bar__sub">{hint}</span>}
						</div>
					</div>

					<div className="bulk-bar__actions">
						<Menu as="div" className="bulk-bar__menu">
							<MenuButton className="bulk-bar__assign" disabled={isAssigning}>
								{isAssigning ? (
									<Spinner size="sm" />
								) : (
									<TagIcon className="w-4 h-4" />
								)}
								<span>{isAssigning ? 'Assigning...' : 'Assign to'}</span>
								<ChevronUpIcon className="w-4 h-4" />
							</MenuButton>

							<Transition
								as={Fragment}
								enter="transition duration-150 ease-out"
								enterFrom="translate-y-1 opacity-0"
								enterTo="translate-y-0 opacity-100"
								leave="transition duration-100 ease-in"
								leaveFrom="opacity-100"
								leaveTo="opacity-0"
							>
								<MenuItems className="bulk-bar__menu-items">
									{categories.length === 0 ? (
										<p className="bulk-bar__menu-empty">
											No categories yet — create one above the list.
										</p>
									) : (
										categories.map(category => (
											<MenuItem key={category.id}>
												<button
													type="button"
													className="bulk-bar__menu-item"
													onClick={() => onAssign(category.id)}
												>
													<span className="truncate">
														{category.name}
													</span>
													<span className="bulk-bar__menu-count">
														{category.serviceCount}
													</span>
												</button>
											</MenuItem>
										))
									)}
								</MenuItems>
							</Transition>
						</Menu>

						<button
							type="button"
							className="bulk-bar__clear"
							onClick={onClear}
							disabled={isAssigning}
							title="Clear selection"
						>
							<XMarkIcon className="w-4 h-4" />
							<span className="hidden sm:inline">Clear</span>
						</button>
					</div>
				</div>
			</div>
		</Transition>
	);
};

export default ServiceBulkBar;
